import { Injectable } from '@angular/core';
import { TaskType } from '@generated/base-types';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import { isDefined } from '@new/common/utils/type-guards/is-defined';
import { TranslateService } from '@ngx-translate/core';
import { Action, State, Store } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { catchError, filter, Observable, switchMap } from 'rxjs';
import { ToastsService } from '../../../../common/services/toasts.service';
import { CreateTaskGQL } from '../../lib/services/tasks/create-task.generated';
import { DeleteTasksGQL } from '../../lib/services/tasks/delete-tasks.generated';
import { LoadTasksGQL } from '../../lib/services/tasks/load-tasks.generated';
import { LoadPool } from '../pool/pool.actions';
import { selectPoolId } from '../pool/pool.state.model';
import { TaskState } from '../task/task.state';
import { ResetTableState as ResetQGItems } from '../task-question-groups/task-question-groups.actions';
import * as Act from './tasks.actions';
import { TasksStateModel, tasksStateDefaults } from './tasks.state.model';

@State<TasksStateModel>({
  name: 'tasks',
  defaults: tasksStateDefaults,
  children: [TaskState]
})
@Injectable()
export class TasksState {
  constructor(
    private readonly loadTasksService: LoadTasksGQL,
    private readonly createTaskService: CreateTaskGQL,
    private readonly deleteTasksService: DeleteTasksGQL,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService,
    private readonly store: Store
  ) {}

  /* Query actions */

  @Action(Act.LoadTasks)
  public loadTasks(ctx: StateContext<TasksStateModel>): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId => this.loadTasksService.fetch({ poolId })),
      takeGraphQLResult(),
      switchMap(res => ctx.dispatch(new Act.LoadTasksSuccess(res.pool.tasks))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadTasksFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadTasksSuccess)
  public loadTasksSuccess(
    ctx: StateContext<TasksStateModel>,
    { tasks }: Act.LoadTasksSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: tasks,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadTasksFailure)
  public loadTasksFailure(
    ctx: StateContext<TasksStateModel>,
    { error }: Act.LoadTasksFailure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'failure', error } }
    });
  }

  @Action(Act.SetSelectedTaskIds)
  public setSelectedTaskIds(
    ctx: StateContext<TasksStateModel>,
    { ids }: Act.SetSelectedTaskIds
  ): void {
    ctx.patchState({ selectedIds: ids });
  }

  /* Mutation actions */

  @Action(Act.Failure)
  public failure(
    ctx: StateContext<TasksStateModel>,
    { error }: Act.Failure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'failure', error } }
    });
    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.CreateTask)
  public createTask(
    ctx: StateContext<TasksStateModel>,
    { attributes, taskType }: Act.CreateTask
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId =>
        this.createTaskService.mutate({ poolId, attributes })
      ),
      takeGraphQLResult(),
      switchMap(() => ctx.dispatch(new Act.CreateTaskSuccess(taskType))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.CreateTaskSuccess)
  public createTaskSuccess(
    ctx: StateContext<TasksStateModel>,
    { taskType }: Act.CreateTaskSuccess
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    const taskNotificationKeyMap: Record<TaskType, string> = {
      [TaskType.Create]: 'create_task_modal',
      [TaskType.Revise]: 'revise_task_modal',
      [TaskType.Comment]: 'comment_task_modal',
      [TaskType.Translate]: 'translate_task_modal'
    };

    this.toasts.addSuccess(
      this.translate.instant(
        `task_management.task_list.${taskNotificationKeyMap[taskType]}.notifications.task_created`
      )
    );

    ctx.dispatch(new Act.LoadTasks());
    const poolId = this.store.selectSnapshot(selectPoolId);
    if (isDefined(poolId)) {
      ctx.dispatch(new LoadPool(poolId));
    }
  }

  @Action(Act.DeleteTasks)
  public deleteTasks(
    ctx: StateContext<TasksStateModel>,
    { taskIds }: Act.DeleteTasks
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId => this.deleteTasksService.mutate({ poolId, taskIds })),
      takeGraphQLResult(),
      switchMap(() => ctx.dispatch(new Act.DeleteTasksSuccess(taskIds))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.DeleteTasksSuccess)
  public deleteTasksSuccess(
    ctx: StateContext<TasksStateModel>,
    { taskIds }: Act.DeleteTasksSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.filter(task => !taskIds.map(Number).includes(task.id)),
        mutation: { state: 'success' }
      },
      selectedIds: []
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.destroy', {
        resource: this.translate.instant('common.models.task')
      })
    );

    ctx.dispatch(new ResetQGItems());
  }
}
